/* eslint-disable lingui/no-unlocalized-strings -- Hostnames and URLs, not UI copy. */

import {
  FIRST_ROUND_INDUSTRY_PAGES,
  FIRST_ROUND_LEGAL_PAGES,
  type FirstRoundLegalPath,
} from "@reactive-resume/utils";

import { withAttributionHandoff } from "@/client/analytics/attribution";

// The First Round marketing hosts are served by this same app, but they are pure marketing pages:
// no Clerk, no sign-in (Clerk's production instance rejects firstround.com.au outright). They show
// the First Round page at "/" plus the industry pages, and every link into the app (sign-in,
// sign-up, dashboard) goes to theresume.io. Everywhere else (theresume.io, staging, localhost)
// links stay relative, so each environment keeps linking to itself.
// Keep in sync with FIRST_ROUND_SITE.hosts in apps/server/src/brand-sites/brand-sites.ts.
const FIRST_ROUND_MARKETING_HOSTS = new Set([
  "firstround.com.au",
  "www.firstround.com.au",
  "firstround.theresume.io",
]);
const THERESUME_APP_ORIGIN = "https://theresume.io";
export const FIRST_ROUND_ORIGIN = "https://firstround.com.au";

/** Query value that makes the sign-in / sign-up screen say "First Round, powered by theResume". */
export const FIRST_ROUND_BRAND_PARAM = "firstround";

const currentHostname = () => (typeof window === "undefined" ? "" : window.location.hostname);

export const isFirstRoundMarketingHost = (hostname = currentHostname()) =>
  FIRST_ROUND_MARKETING_HOSTS.has(hostname);

/** Tag auth links with ?brand=firstround so the login screen shows the First Round line. */
const withBrand = (path: string) => {
  if (!path.startsWith("/auth/") || /[?&]brand=/.test(path)) return path;
  return `${path}${path.includes("?") ? "&" : "?"}brand=${FIRST_ROUND_BRAND_PARAM}`;
};

export const toFirstRoundAppUrl = (path: string, hostname = currentHostname()) =>
  isFirstRoundMarketingHost(hostname)
    ? withAttributionHandoff(`${THERESUME_APP_ORIGIN}${withBrand(path)}`)
    : withBrand(path);

/**
 * First Round's home for the current environment: the brand domain in production, the in-app
 * marketing route on staging and localhost, which have no firstround.com.au deployment. Used by
 * the sign-in screen's logo and "Back home" link so a First Round visitor goes back where they
 * came from instead of landing on theResume's homepage.
 */
export const firstRoundHomeUrl = (hostname = currentHostname()) =>
  hostname === "theresume.io" || isFirstRoundMarketingHost(hostname)
    ? withAttributionHandoff(`${FIRST_ROUND_ORIGIN}/`)
    : "/first-round";

/**
 * Pages that moved from theresume.io to firstround.com.au. The server 301s full page loads; this
 * covers in-app navigation to the old paths. Only production theresume.io redirects, so staging
 * and localhost keep serving the pages.
 */
const MOVED_TO_FIRST_ROUND: Record<string, string> = {
  "/first-round": "/",
  "/practice-interview": "/",
  ...Object.fromEntries(
    Object.keys(FIRST_ROUND_LEGAL_PAGES).map((path) => [`/first-round${path}`, path]),
  ),
  ...Object.fromEntries(Object.keys(FIRST_ROUND_INDUSTRY_PAGES).map((path) => [path, path])),
};

export const movedToFirstRoundUrl = (pathname: string, hostname = currentHostname()) => {
  if (hostname !== "theresume.io") return undefined;
  const target = MOVED_TO_FIRST_ROUND[pathname.replace(/\/+$/, "") || "/"];
  return target === undefined ? undefined : `${FIRST_ROUND_ORIGIN}${target}`;
};

/** The signed-in page on theresume.io with the visitor's demo transcript and feedback. */
export const demoResultsPath = (sessionId: string) => `/demo/practice/${sessionId}`;

/**
 * Where the "see your feedback" buttons go when the demo ends: the results page, which sends a
 * signed-out visitor to the First Round-branded sign-up (or sign-in) and back. Absolute on the
 * marketing hosts, relative elsewhere so each environment keeps linking to itself.
 */
export const demoResultsUrl = (
  sessionId: string,
  auth: "signup" | "signin",
  hostname = currentHostname(),
) => {
  const path = `${demoResultsPath(sessionId)}?brand=${FIRST_ROUND_BRAND_PARAM}&auth=${auth}`;
  return isFirstRoundMarketingHost(hostname)
    ? withAttributionHandoff(`${THERESUME_APP_ORIGIN}${path}`)
    : path;
};

/** Whether a link target is one of the First Round legal pages (/legal, /terms, /privacy...). */
export const isFirstRoundLegalPath = (target: string): target is FirstRoundLegalPath =>
  target in FIRST_ROUND_LEGAL_PAGES;

/**
 * A First Round legal page for the current environment: the brand domain in production (relative
 * on the marketing hosts, absolute from theresume.io, e.g. the sign-in screen), the in-app copy
 * at /first-round<path> on staging and localhost. Append a "#section-N" anchor as needed.
 */
export const firstRoundLegalUrl = (path: FirstRoundLegalPath, hostname = currentHostname()) => {
  if (isFirstRoundMarketingHost(hostname)) return path;
  return hostname === "theresume.io" ? `${FIRST_ROUND_ORIGIN}${path}` : `/first-round${path}`;
};
